import React, { useState, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import { useTranslation } from 'react-i18next';
import {
  Box, Table, TableBody, Menu, TableCell, TableContainer, TableHead, TableRow, Typography, Button, Modal, Select, FormControl, MenuItem, Pagination, IconButton, CircularProgress, Alert, TextField, Card, CardContent, useMediaQuery, useTheme, Autocomplete, Chip
} from '@mui/material';
import { styled } from '@mui/system';
import CloseIcon from '@mui/icons-material/Close';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useParams } from 'react-router-dom';
import myImage from '../../assets/images/img.png';
import BookIcon from '@mui/icons-material/Book';
import BrokerModal from '../modal_broker/BrokerModal';
import WabaModal from '../modal_waba/WabaModal';
import ErrorManager from '../ErrorManager';
import InstaModal from '../instagram/instaModal';
import TriggerDetailsModal from '../TriggerDetailsModal';
import DuplicateModals from '../DuplicateModals';
import useDuplication from '../../hooks/useDuplication';
import { fetchChannels, cancelTrigger, fetchTriggersAdvanced, interruptTrigger, fetchUsersPaginated, fetchQuickMessagesPaginated } from '../../../src/application/service/apiService';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AssessmentIcon from '@mui/icons-material/Assessment';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import CancelIcon from '@mui/icons-material/Cancel';
import StopIcon from '@mui/icons-material/Stop';
import brokerIcon from '../../assets/images/brokerIcon.svg';
import instaIcon from '../../assets/images/insta.svg';
import './Disparador.css';

const DisparadorContainer = styled(Box)({
  flexGrow: 1,
  padding: '20px',
  backgroundColor: '#F5F6F7',
});

const getStatusColor = (statusId) => {
  const colors = {
    1: { bg: '#28a745', hover: '#218838' },       // Concluído
    2: { bg: '#dc3545', hover: '#c82333' },       // Falha
    3: { bg: '#ffc107', hover: '#e0a800' },       // Em execução
    0: { bg: '#17a2b8', hover: '#138496' },       // Pendente
    4: { bg: '#007bff', hover: '#0056b3' },       // Agendado
    5: { bg: '#6c757d', hover: '#5a6268' },       // Cancelado
    6: { bg: '#ff5733', hover: '#e04e1f' },       // Interrompido
  };
  return colors[statusId] || { bg: '#dc3545', hover: '#c82333' };
};

const StatusButton = styled(Button)(({ statusId }) => {
  const colors = getStatusColor(statusId);
  return {
    backgroundColor: colors.bg,
    color: '#fff',
    borderRadius: '12px',
    padding: '5px 10px',
    textTransform: 'none',
    minWidth: '100px',
    maxWidth: '150px',
    height: '35px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    fontSize: '14px',
    '&:hover': {
      backgroundColor: colors.hover,
    },
    '@media (max-width: 600px)': {
      minWidth: '80px',
      maxWidth: '120px',
      fontSize: '12px',
      padding: '4px 8px',
    },
  };
});

const Disparador = () => {
  const { t } = useTranslation();
  const { acontID, token } = useParams();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [rows, setRows] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedChannel, setSelectedChannel] = useState(null);
  const [secondaryModalOpen, setSecondaryModalOpen] = useState(false);
  const [createModalChannelSearch, setCreateModalChannelSearch] = useState('');
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedTriggerDetails, setSelectedTriggerDetails] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isEmpty, setIsEmpty] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [channels, setChannels] = useState([]);
  const errorManagerRef = useRef(null);
  const [statusId, setStatusId] = useState('');
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [selectedTriggerId, setSelectedTriggerId] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [interruptModalOpen, setInterruptModalOpen] = useState(false);
  const [isInterrupting, setIsInterrupting] = useState(false);
  const [isCanceling, setIsCanceling] = useState(false);
  const [isDownloading, setIsDownloading] = useState({});
  const [isAutoRefreshing, setIsAutoRefreshing] = useState(false);
  const autoRefreshIntervalRef = useRef(null);

  // Estados dos filtros avançados
  const [advancedFiltersOpen, setAdvancedFiltersOpen] = useState(false);
  const [advancedCampaignName, setAdvancedCampaignName] = useState('');
  const [advancedChannelName, setAdvancedChannelName] = useState('');
  const [advancedSelectedUsers, setAdvancedSelectedUsers] = useState([]);
  const [advancedSelectedTemplates, setAdvancedSelectedTemplates] = useState([]);
  const [draftCampaignName, setDraftCampaignName] = useState('');
  const [draftChannelName, setDraftChannelName] = useState('');
  const [draftSelectedUsers, setDraftSelectedUsers] = useState([]);
  const [draftSelectedTemplates, setDraftSelectedTemplates] = useState([]);
  const [advancedUsersOptions, setAdvancedUsersOptions] = useState([]);
  const [advancedTemplatesOptions, setAdvancedTemplatesOptions] = useState([]);
  const [isLoadingUsersOptions, setIsLoadingUsersOptions] = useState(false);
  const [isLoadingTemplatesOptions, setIsLoadingTemplatesOptions] = useState(false);
  
  // Paginação para usuários e templates
  const [usersPage, setUsersPage] = useState(1);
  const [usersLastPage, setUsersLastPage] = useState(1);
  const [usersSearchTerm, setUsersSearchTerm] = useState('');
  const [templatesPage, setTemplatesPage] = useState(1);
  const [templatesLastPage, setTemplatesLastPage] = useState(1);
  const [templatesSearchTerm, setTemplatesSearchTerm] = useState('');
  const [isLoadingMoreUsers, setIsLoadingMoreUsers] = useState(false);
  const [isLoadingMoreTemplates, setIsLoadingMoreTemplates] = useState(false);
  
  // Paginação para canais
  const [channelsPage, setChannelsPage] = useState(1);
  const [channelsLastPage, setChannelsLastPage] = useState(1);
  const [isLoadingMoreChannels, setIsLoadingMoreChannels] = useState(false);
  
  // Estados para controle de roles do usuário
  const [isOperadorOnly, setIsOperadorOnly] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userRoles, setUserRoles] = useState([]);

  const today = new Date().toISOString().split('T')[0];

  const handleStartDateChange = (e) => {
    const selectedDate = e.target.value;

    if (selectedDate > today) {
      setError(t('disparador.error.invalidStartDate'));
    } else {
      setError('');
      setStartDate(selectedDate);

      if (endDate && selectedDate > endDate) {
        setEndDate('');
      }
    }
  };

  const handleMenuClick = (event, row) => {
    setOpenMenuId(row.id);
    setAnchorEl(event.currentTarget);
    setSelectedRow(row);
  };

  const handleMenuClose = () => {
    setOpenMenuId(null);
    setAnchorEl(null);
    setSelectedRow(null);
  };

  useEffect(() => {
    if (statusId) {
      setCurrentPage(1);
      // eslint-disable-next-line react-hooks/exhaustive-deps
      fetchData(1, false, true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusId]);

  const handleEndDateChange = (e) => {
    const selectedDate = e.target.value;

    if (selectedDate < startDate) {
      setError(t('disparador.error.invalidEndDate'));
    } else {
      setError('');
      setEndDate(selectedDate);
    }
  };

  const handleStatusChange = (e) => {
    setStatusId(e.target.value);
  };

  const getStatus = (status_id) => {
    if (status_id === 1) return t('disparador.status.completed');
    if (status_id === 2) return t('disparador.status.failure');
    if (status_id === 3) return t('disparador.status.running');
    if (status_id === 0) return t('disparador.status.pending');
    if (status_id === 4) return t('disparador.status.scheduled');
    if (status_id === 5) return t('disparador.status.canceled');
    if (status_id === 6) return t('disparador.status.interrupted');
    return t('disparador.status.error');
  };

  const fetchData = async (page, resetDates = false, resetPagination = false, isAutoRefresh = false) => {
    void resetDates;
    void resetPagination;

    if (isAutoRefresh) {
      setIsAutoRefreshing(true);
    } else {
      setIsLoading(true);
    }
    
    try {
      const filters = {
        start_date: startDate ? `${startDate} 00:00:01` : undefined,
        end_date: endDate ? `${endDate} 23:59:00` : undefined,
        status_id: statusId !== '' ? [statusId] : [],
        trigger_identify: advancedCampaignName?.trim() || undefined,
        channel_name: advancedChannelName?.trim() || undefined,
        user_id: advancedSelectedUsers.map((user) => user.id),
        quick_message_id: advancedSelectedTemplates.map((template) => template.id),
      };

      const data = await fetchTriggersAdvanced(acontID, token, filters, page);

      setRows(data.data || []);
      setTotalPages(data.last_page);
      setIsEmpty(data.data.length === 0);
    } catch (error) {
      console.error('Error fetching data:', error);
      setIsEmpty(true);
    } finally {
      if (isAutoRefresh) {
        setIsAutoRefreshing(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    const startAutoRefresh = () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }

      autoRefreshIntervalRef.current = setInterval(() => {
        // eslint-disable-next-line react-hooks/exhaustive-deps
        fetchData(currentPage, false, false, true);
      }, 20 * 1000);
    };

    startAutoRefresh();

    return () => {
      if (autoRefreshIntervalRef.current) {
        clearInterval(autoRefreshIntervalRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, startDate, endDate, statusId, advancedCampaignName, advancedChannelName, advancedSelectedUsers, advancedSelectedTemplates, acontID, token]);

  useEffect(() => {
    if (!token) {
      console.error('Token is missing');
      return;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
    fetchData(currentPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [acontID, token, currentPage, startDate, endDate, statusId, advancedCampaignName, advancedChannelName, advancedSelectedUsers, advancedSelectedTemplates]);

  // Função para buscar roles do usuário
  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_URL_DISP}/get-roles/user`, {
          headers: {
            'accept': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Erro ao buscar roles do usuário');
        }

        const data = await response.json();
        
        // Armazena todos os roles
        setUserRoles(data.roles || []);

        // Verifica se o usuário tem apenas role_id = 2 (operador de chat)
        const hasOnlyOperadorRole = data.roles.length === 1 && data.roles[0].role_id === 2;
        setIsOperadorOnly(hasOnlyOperadorRole);

        // Armazena o user_id para pré-seleção
        if (hasOnlyOperadorRole && data.roles[0].user_id) {
          setCurrentUserId(data.roles[0].user_id);
        }
      } catch (error) {
        console.error('Erro ao buscar roles:', error.message);
        // Não mostrar erro ao usuário, apenas logar no console
      }
    };

    if (token) {
      fetchUserRoles();
    }
  }, [token]);

  useEffect(() => {
    const loadChannels = async () => {
      try {
        const response = await fetchChannels(acontID, token, 1, 50);
        setChannels(response.channels);
        setChannelsPage(1);
        setChannelsLastPage(response.last_page || 1);
      } catch (error) {
        console.error('Erro ao buscar os canais:', error);
        errorManagerRef.current.showError(t('disparador.error.loadChannelsError'));
      }
    };

    loadChannels();
  }, [acontID, token, t]);

  const handleOpenAdvancedFilters = () => {
    setDraftCampaignName(advancedCampaignName);
    setDraftChannelName(advancedChannelName);
    setDraftSelectedUsers(advancedSelectedUsers);
    setDraftSelectedTemplates(advancedSelectedTemplates);
    setUsersSearchTerm('');
    setUsersPage(1);
    setUsersLastPage(1);
    setTemplatesSearchTerm('');
    setTemplatesPage(1);
    setTemplatesLastPage(1);
    setAdvancedFiltersOpen(true);
  };

  const handleCloseAdvancedFilters = () => {
    setAdvancedFiltersOpen(false);
  };

  // Handlers para scroll infinito
  const handleUsersListboxScroll = (event) => {
    const listboxNode = event.currentTarget;

    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
      usersPage < usersLastPage &&
      !isLoadingMoreUsers &&
      !isLoadingUsersOptions
    ) {
      setIsLoadingMoreUsers(true);
      loadUsersPage(usersPage + 1, usersSearchTerm).finally(() => setIsLoadingMoreUsers(false));
    }
  };

  const handleTemplatesListboxScroll = (event) => {
    const listboxNode = event.currentTarget;

    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
      templatesPage < templatesLastPage &&
      !isLoadingMoreTemplates &&
      !isLoadingTemplatesOptions
    ) {
      setIsLoadingMoreTemplates(true);
      loadTemplatesPage(templatesPage + 1, templatesSearchTerm).finally(() => setIsLoadingMoreTemplates(false));
    }
  };

  const handleApplyAdvancedFilters = () => {
    setAdvancedCampaignName(draftCampaignName.trim());
    setAdvancedChannelName(draftChannelName.trim());
    setAdvancedSelectedUsers(draftSelectedUsers);
    setAdvancedSelectedTemplates(draftSelectedTemplates);
    setCurrentPage(1);
    setAdvancedFiltersOpen(false);
  };

  const loadUsersPage = async (page = 1, searchTerm = '') => {
    if (page === 1) {
      setIsLoadingUsersOptions(true);
    }

    try {
      const result = await fetchUsersPaginated(acontID, token, page, 20, searchTerm);

      if (page === 1) {
        setAdvancedUsersOptions(result.data.sort((a, b) => a.name.localeCompare(b.name)));
      } else {
        setAdvancedUsersOptions((prev) => {
          const combined = [...prev, ...result.data];
          const uniqueMap = new Map();
          combined.forEach((user) => uniqueMap.set(user.id, user));
          return Array.from(uniqueMap.values()).sort((a, b) => a.name.localeCompare(b.name));
        });
      }

      setUsersPage(result.current_page);
      setUsersLastPage(result.last_page);
    } catch (loadError) {
      console.error('Erro ao carregar usuários:', loadError);
    } finally {
      if (page === 1) {
        setIsLoadingUsersOptions(false);
      }
    }
  };

  const debouncedSearchUsers = useMemo(
    () =>
      debounce((searchTerm) => {
        setUsersSearchTerm(searchTerm);
        loadUsersPage(1, searchTerm);
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acontID, token]
  );

  useEffect(() => {
    return () => debouncedSearchUsers.cancel();
  }, [debouncedSearchUsers]);

  const handleUsersInputChange = (event, value, reason) => {
    if (reason !== 'input') return;

    if (!value) {
      debouncedSearchUsers.cancel();
      setUsersSearchTerm('');
      loadUsersPage(1, '');
      return;
    }

    debouncedSearchUsers(value);
  };

  const loadTemplatesPage = async (page = 1, searchTerm = '') => {
    if (page === 1) {
      setIsLoadingTemplatesOptions(true);
    }

    try {
      const result = await fetchQuickMessagesPaginated(acontID, token, page, 20, searchTerm);

      if (page === 1) {
        setAdvancedTemplatesOptions(result.data.sort((a, b) => a.tag.localeCompare(b.tag)));
      } else {
        setAdvancedTemplatesOptions((prev) => {
          const combined = [...prev, ...result.data];
          const uniqueMap = new Map();
          combined.forEach((template) => uniqueMap.set(template.id, template));
          return Array.from(uniqueMap.values()).sort((a, b) => a.tag.localeCompare(b.tag));
        });
      }

      setTemplatesPage(result.current_page);
      setTemplatesLastPage(result.last_page);
    } catch (loadError) {
      console.error('Erro ao carregar templates:', loadError);
    } finally {
      if (page === 1) {
        setIsLoadingTemplatesOptions(false);
      }
    }
  };

  const debouncedSearchTemplates = useMemo(
    () =>
      debounce((searchTerm) => {
        setTemplatesSearchTerm(searchTerm);
        loadTemplatesPage(1, searchTerm);
      }, 300),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [acontID, token]
  );

  useEffect(() => {
    return () => debouncedSearchTemplates.cancel();
  }, [debouncedSearchTemplates]);

  const handleTemplatesInputChange = (event, value, reason) => {
    if (reason !== 'input') return;

    if (!value) {
      debouncedSearchTemplates.cancel();
      setTemplatesSearchTerm('');
      loadTemplatesPage(1, '');
      return;
    }

    debouncedSearchTemplates(value);
  };

  useEffect(() => {
    if (!advancedFiltersOpen) return;

    loadUsersPage(1, '');
    loadTemplatesPage(1, '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [advancedFiltersOpen, token, acontID]);

  // Função para carregar mais canais com paginação
  const loadMoreChannels = async (page) => {
    if (page > channelsLastPage || isLoadingMoreChannels) return;

    setIsLoadingMoreChannels(true);
    try {
      const response = await fetchChannels(acontID, token, page, 50);
      const newChannels = response.channels;

      setChannels(prev => {
        const uniqueMap = new Map();
        [...prev, ...newChannels].forEach(channel => uniqueMap.set(channel.id, channel));
        return Array.from(uniqueMap.values());
      });

      setChannelsPage(page);
      setChannelsLastPage(response.last_page || page);
    } catch (error) {
      console.error('Erro ao carregar mais canais:', error);
    } finally {
      setIsLoadingMoreChannels(false);
    }
  };

  // Handler para scroll infinito no Select de canais
  const handleChannelsMenuScroll = (event) => {
    const listboxNode = event.currentTarget;
    const nextPage = channelsPage + 1;

    if (
      listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10 &&
      nextPage <= channelsLastPage &&
      !isLoadingMoreChannels
    ) {
      loadMoreChannels(nextPage);
    }
  };

  // Hook para gerenciar duplicação
  const {
    duplicateModalOpen,
    duplicateWabaModalOpen,
    duplicateBrokerModalOpen,
    duplicateDateTime,
    setDuplicateDateTime,
    isDuplicating,
    wabaTemplates,
    selectedTemplate,
    setSelectedTemplate,
    // Estados específicos do BROKER
    brokerMensagem,
    setBrokerMensagem,
    brokerMensagemRapida,
    setBrokerMensagemRapida,
    brokerMensagensRapidas,
    brokerImagem,
    setBrokerImagem,
    brokerImagemUrl,
    setBrokerImagemUrl,
    wabaChannelId,
    brokerChannelId,
    handleDuplicateClick,
    handleDuplicateModalClose,
    handleConfirmDuplicate,
    handleConfirmDuplicateWaba,
    handleConfirmDuplicateBroker
  } = useDuplication(errorManagerRef, fetchData, currentPage);

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleCreateClick = () => {
    setModalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setSelectedChannel(null);
    setCreateModalChannelSearch('');
  };

  // Função para buscar canais na API por nome
  const searchChannelsByName = async (name) => {
    if (!name.trim()) {
      // Se vazio, carrega todos os canais (primeira página)
      try {
        const response = await fetchChannels(acontID, token, 1, 50);
        setChannels(response.channels);
        setChannelsPage(1);
        setChannelsLastPage(response.last_page || 1);
      } catch (error) {
        console.error('Erro ao buscar canais:', error);
      }
    } else {
      // Se tem texto, busca pela API
      try {
        setIsLoadingMoreChannels(true);
        const response = await fetchChannels(acontID, token, 1, 50, name);
        setChannels(response.channels);
        setChannelsPage(1);
        setChannelsLastPage(response.last_page || 1);
      } catch (error) {
        console.error('Erro ao buscar canais:', error);
      } finally {
        setIsLoadingMoreChannels(false);
      }
    }
  };

  // Debounce para busca de canais no modal de criar
  useEffect(() => {
    if (!modalOpen) return;

    const timer = setTimeout(() => {
      searchChannelsByName(createModalChannelSearch);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createModalChannelSearch, modalOpen]);


  const closeSecondaryModal = () => {
    setSecondaryModalOpen(false);
    setSelectedChannel(null);
  };

  const handleBookIconClick = (details) => {
    setSelectedTriggerDetails(details);
    setDetailsModalOpen(true);
  };

  const closeDetailsModal = () => {
    setDetailsModalOpen(false);
    setSelectedTriggerDetails(null);
  };

  // Apenas Administrador geral e Gestor podem cancelar/interromper disparos
  const canManageDispatch = () => userRoles?.some(
    role => role.role_id === 1 || role.role_id === 3 || role.name === 'adm' || role.name === 'manager'
  );

  const handleCancelClick = (triggerId) => {
    if (!canManageDispatch()) {
      errorManagerRef.current?.showError(t('disparador.error.onlyManagerCanCancel') || 'Apenas gestores podem cancelar disparos, solicite o cancelamento para seu gestor');
      return;
    }
    
    setSelectedTriggerId(triggerId);
    setCancelModalOpen(true);
  };

  const handleCancelModalClose = () => {
    setCancelModalOpen(false);
    setSelectedTriggerId(null);
  };

  const handleConfirmCancel = async () => {
    setIsCanceling(true);
    try {
      await cancelTrigger(selectedTriggerId, acontID, token);
      setCancelModalOpen(false);
      setSelectedTriggerId(null);
      fetchData(currentPage);
    } catch (error) {
      console.error('Erro ao cancelar o trigger:', error);
    } finally {
      setIsCanceling(false);
    }
  };

  const handleInterruptClick = (triggerId) => {
    if (!canManageDispatch()) {
      errorManagerRef.current?.showError(t('disparador.error.onlyManagerCanCancel') || 'Apenas gestores podem cancelar disparos, solicite o cancelamento para seu gestor');
      return;
    }
    
    setSelectedTriggerId(triggerId);
    setInterruptModalOpen(true);
  };

  const handleInterruptModalClose = () => {
    setInterruptModalOpen(false);
    setSelectedTriggerId(null);
  };

  const handleConfirmInterrupt = async () => {
    setIsInterrupting(true);
    try {
      await interruptTrigger(acontID, token);
      await new Promise(resolve => setTimeout(resolve, 15000));
      setInterruptModalOpen(false);
      setSelectedTriggerId(null);
      setAnchorEl(null);
      setSelectedRow(null);
      fetchData(currentPage);
    } catch (error) {
      errorManagerRef.current.showError(t('disparador.error.interruptError'));
    } finally {
      setIsInterrupting(false);
    }
  };

  const handleDownloadClick = async (triggerId, linkFile) => {
    if (!linkFile) return;
    setIsDownloading(prev => ({ ...prev, [triggerId]: true }));
    try {
      const urlObj = new URL(linkFile);
      const expires = parseInt(urlObj.searchParams.get('X-Amz-Expires'), 10);
      const dateStr = urlObj.searchParams.get('X-Amz-Date');
      if (expires && dateStr) {
        const date = new Date(dateStr.replace(/(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/, '$1-$2-$3T$4:$5:$6Z'));
        const expirationTime = date.getTime() + expires * 1000;
        if (Date.now() > expirationTime) {
          errorManagerRef.current.showError(t('disparador.error.notAvailable'));
          return;
        }
      }
      const link = document.createElement('a');
      link.href = linkFile;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      errorManagerRef.current.showError(t('disparador.error.downloadError'));
    } finally {
      setTimeout(() => {
        setIsDownloading(prev => ({ ...prev, [triggerId]: false }));
      }, 500);
    }
  };

  const handleExportReportClick = async (linkFile) => {
    if (!linkFile) {
      errorManagerRef.current.showError(t('disparador.error.reportNotAvailable'));
      return;
    }
    
    try {
      const link = document.createElement('a');
      link.href = linkFile;
      link.download = '';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      handleMenuClose();
    } catch (error) {
      errorManagerRef.current.showError(t('disparador.error.exportError'));
    }
  };

  const activeAdvancedFiltersCount = [
    advancedCampaignName,
    advancedChannelName,
    advancedSelectedUsers.length,
    advancedSelectedTemplates.length,
  ].filter(Boolean).length;

  const selectedChipSx = {
    backgroundColor: '#7EDFE5',
    color: '#1F4E5A',
    borderRadius: '6px',
    height: '26px',
    '& .MuiChip-label': {
      px: 1,
      fontSize: '12px',
      fontWeight: 500,
    },
    '& .MuiChip-deleteIcon': {
      color: '#245968',
      fontSize: '16px',
    },
  };

  return (
    <>
      <ErrorManager ref={errorManagerRef} />
      <DisparadorContainer>
        <Box display="flex" justifyContent="space-between" alignItems={isMobile ? "flex-start" : "center"} mb={3} flexDirection={isMobile ? "column" : "row"} gap={isMobile ? 2 : 0}>
          <Box display="flex" flexDirection="column" gap={1} width={isMobile ? "100%" : "auto"}>
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Typography variant="subtitle1" color="textSecondary">
                <strong>{t('disparador.filterByDate')}</strong>
              </Typography>
              {isAutoRefreshing && (
                <Box display="flex" alignItems="center" gap={1}>
                  <CircularProgress size={16} sx={{ color: '#1976d2' }} />
                  <Typography variant="caption" color="textSecondary">
                    {t('disparador.updating')}
                  </Typography>
                </Box>
              )}
            </Box>
            <Box display="flex" gap={1} flexDirection={isSmallMobile ? "column" : "row"} flexWrap="wrap">
              <TextField
                type="date"
                value={startDate}
                onChange={handleStartDateChange}
                max={today}
                variant="outlined"
                size="small"
                fullWidth={isSmallMobile}
                sx={{
                  minWidth: isSmallMobile ? "100%" : "140px",
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
              <TextField
                type="date"
                value={endDate}
                onChange={handleEndDateChange}
                min={startDate}
                variant="outlined"
                size="small"
                fullWidth={isSmallMobile}
                sx={{
                  minWidth: isSmallMobile ? "100%" : "140px",
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&:hover fieldset': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#1976d2',
                    },
                  },
                }}
              />
              <FormControl variant="outlined" size="small" fullWidth={isSmallMobile} sx={{ minWidth: isSmallMobile ? "100%" : "150px" }}>
                <Select
                  value={statusId}
                  onChange={handleStatusChange}
                  displayEmpty
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#1976d2',
                    },
                  }}
                >
                  <MenuItem value="">{t('disparador.allStatus')}</MenuItem>
                  <MenuItem value="0">{t('disparador.status.pending')}</MenuItem>
                  <MenuItem value="1">{t('disparador.status.completed')}</MenuItem>
                  <MenuItem value="2">{t('disparador.status.failure')}</MenuItem>
                  <MenuItem value="3">{t('disparador.status.running')}</MenuItem>
                  <MenuItem value="4">{t('disparador.status.scheduled')}</MenuItem>
                  <MenuItem value="5">{t('disparador.status.canceled')}</MenuItem>
                  <MenuItem value="6">{t('disparador.status.interrupted')}</MenuItem>
                </Select>
              </FormControl>
              <Button
                variant="outlined"
                onClick={handleOpenAdvancedFilters}
                startIcon={<FilterListIcon />}
                fullWidth={isSmallMobile}
                sx={{
                  minWidth: isSmallMobile ? '100%' : '170px',
                  borderColor: '#1976d2',
                  color: '#1976d2',
                  fontWeight: 600,
                }}
              >
                {(t('disparador.advancedFilters') || 'Filtros avançados') + (activeAdvancedFiltersCount ? ` (${activeAdvancedFiltersCount})` : '')}
              </Button>
            </Box>
            {error && <Alert severity="error">{error}</Alert>}
          </Box>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={handleCreateClick}
            fullWidth={isMobile}
            sx={{ 
              minWidth: isMobile ? "100%" : "auto",
              mt: isMobile ? 1 : 0,
              backgroundColor: "#0000C8",
              "&:hover": {
                backgroundColor: "#0000A0"
              }
            }}
          >
            {isSmallMobile ? t('disparador.newCampaign') : t('disparador.createNewCampaign')}
          </Button>
        </Box>

        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" height="70vh">
            <CircularProgress style={{ color: '#1976d2' }} />
          </Box>
        ) : (
          <>
            {isEmpty ? (
              <Box display="flex" flexDirection="column" justifyContent="center" alignItems="center" height="70vh">
                <img src={myImage} alt="No Data" style={{ width: '500px', height: 'auto' }} />
                <Typography variant="h6" color="textSecondary" mt={2}>
                  {t('disparador.noDataFound')}
                </Typography>
              </Box>
            ) : (
              <>
                {/* Desktop Table View */}
                {!isMobile ? (
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>{t('disparador.table.campaign')}</strong></TableCell>
                          <TableCell><strong>{t('disparador.table.tagName')}</strong></TableCell>
                          <TableCell><strong>{t('disparador.table.channel')}</strong></TableCell>
                          <TableCell><strong>{t('disparador.table.sendDate')}</strong></TableCell>
                          <TableCell><strong>{t('disparador.table.responsible')}</strong></TableCell>
                          <TableCell align="center"><strong>{t('disparador.table.totalDispatches')}</strong></TableCell>
                          <TableCell align="center"><strong><CheckCircleIcon sx={{ fontSize: '16px', verticalAlign: 'middle', mr: 0.5, color: '#2e7d32' }} />{t('disparador.table.success')}</strong></TableCell>
                          <TableCell align="center"><strong><ErrorIcon sx={{ fontSize: '16px', verticalAlign: 'middle', mr: 0.5, color: '#d32f2f' }} />{t('disparador.table.failure')}</strong></TableCell>
                          <TableCell><strong>{t('disparador.table.status')}</strong></TableCell>
                          <TableCell><strong>{t('disparador.table.actions')}</strong></TableCell>
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {rows.map((row) => {
                          // eslint-disable-next-line no-unused-vars
                          const isDownloadDisabled = row.started_at
                            ? new Date(row.started_at) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                            : true; // Desativa se started_at for null
                          return (
                            <TableRow key={row.id} hover>
                              <TableCell>{row.trigger_identify}</TableCell>
                              <TableCell>{row.entry_tag || ''}</TableCell>
                              <TableCell>
                                <Box display="flex" alignItems="center">
                                  {(row.trigger_type === "BROKER" || row.trigger_type === "WABA") && <img src={brokerIcon} alt="Broker Icon" style={{ marginRight: '8px' }} />}
                                  {row.trigger_type === "INSTAGRAM" && <img src={instaIcon} alt="Instagram Icon" style={{ marginRight: '8px' }} />}
                                  <Typography>{row.channel_name || 'N/A'}</Typography>
                                </Box>
                              </TableCell>
                              <TableCell>{new Date(row.schedule_at || row.started_at || 'N/A').toLocaleString()}</TableCell>
                              <TableCell>{row.user_name || 'N/A'}</TableCell>
                              <TableCell className="metrics-column" align="center">
                                <Typography variant="body2" className="total-count">
                                  {row.total_count || '0'}
                                </Typography>
                              </TableCell>
                              <TableCell className="metrics-column" align="center">
                                <Typography variant="body2" className="success-count">
                                  {row.success_count || '0'}
                                </Typography>
                              </TableCell>
                              <TableCell className="metrics-column" align="center">
                                <Typography variant="body2" className="failure-count">
                                  {row.failure_count || '0'}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <StatusButton statusId={row.status_id} className={`StatusButton ${getStatus(row.status_id).toUpperCase()}`}>
                                  {getStatus(row.status_id)}
                                </StatusButton>
                              </TableCell>
                              <TableCell>
                                <IconButton 
                                  onClick={(event) => handleMenuClick(event, row)}
                                  size="small"
                                >
                                  <MoreVertIcon />
                                </IconButton>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  /* Mobile Card View */
                  <Box display="flex" flexDirection="column" gap={2}>
                    {rows.map((row) => {
                      // eslint-disable-next-line no-unused-vars
                      const isDownloadDisabled = row.started_at
                        ? new Date(row.started_at) < new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                        : true;
                      return (
                        <Card 
                          key={row.id} 
                          sx={{ 
                            '&:hover': {
                              boxShadow: 3,
                              transform: 'translateY(-1px)',
                              transition: 'all 0.2s ease-in-out'
                            }
                          }}
                          className="campaign-card"
                        >
                          <CardContent sx={{ padding: isSmallMobile ? '12px !important' : '16px !important' }}>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2} flexDirection={isSmallMobile ? "column" : "row"} gap={isSmallMobile ? 1 : 0}>
                              <Typography variant={isSmallMobile ? "subtitle1" : "h6"} component="div" sx={{ 
                                maxWidth: isSmallMobile ? "100%" : "70%",
                                fontSize: isSmallMobile ? "1rem" : "1.25rem",
                                lineHeight: isSmallMobile ? "1.2" : "1.6",
                                wordBreak: "break-word"
                              }}>
                                {row.trigger_identify}
                              </Typography>
                              <Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap" justifyContent={isSmallMobile ? "space-between" : "flex-end"} width={isSmallMobile ? "100%" : "auto"}>
                                <StatusButton statusId={row.status_id} size="small" sx={{ fontSize: isSmallMobile ? "0.75rem" : "0.875rem", minWidth: isSmallMobile ? "70px" : "80px", height: isSmallMobile ? "24px" : "32px" }}>
                                  {getStatus(row.status_id)}
                                </StatusButton>
                                <Box display="flex" alignItems="center" gap={0.5}>
                                  <IconButton 
                                    onClick={(event) => handleMenuClick(event, row)}
                                    size="small"
                                    sx={{ padding: isSmallMobile ? "4px" : "8px" }}
                                  >
                                    <MoreVertIcon sx={{ fontSize: isSmallMobile ? "16px" : "20px" }} />
                                  </IconButton>
                                </Box>
                              </Box>
                            </Box>

                            <Box display="flex" alignItems="center" mb={1} gap={1}>
                              {(row.trigger_type === "BROKER" || row.trigger_type === "WABA") && 
                                <img src={brokerIcon} alt="Broker Icon" style={{ width: isSmallMobile ? '14px' : '16px', height: isSmallMobile ? '14px' : '16px' }} />}
                              {row.trigger_type === "INSTAGRAM" && 
                                <img src={instaIcon} alt="Instagram Icon" style={{ width: isSmallMobile ? '14px' : '16px', height: isSmallMobile ? '14px' : '16px' }} />}
                              <Typography variant="body2" color="textSecondary" sx={{ fontSize: isSmallMobile ? "0.75rem" : "0.875rem" }}>
                                {row.channel_name || 'N/A'}
                              </Typography>
                            </Box>

                            <Typography variant="body2" color="textSecondary" mb={1} sx={{ fontSize: isSmallMobile ? "0.75rem" : "0.875rem" }}>
                              📅 {new Date(row.schedule_at || row.started_at || 'N/A').toLocaleDateString('pt-BR', {
                                day: '2-digit',
                                month: '2-digit',
                                year: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit'
                              })}
                            </Typography>

                            <Typography variant="body2" color="textSecondary" mb={2} sx={{ fontSize: isSmallMobile ? "0.75rem" : "0.875rem" }}>
                              👤 {row.user_name || 'N/A'}
                            </Typography>

                            {row.entry_tag && (
                              <Typography variant="body2" color="textSecondary" mb={2} sx={{ fontSize: isSmallMobile ? "0.75rem" : "0.875rem" }}>
                                🏷️ {row.entry_tag}
                              </Typography>
                            )}

                            <Box display="flex" justifyContent="space-around" sx={{ bgcolor: 'grey.50', p: isSmallMobile ? 0.75 : 1, borderRadius: 1 }}>
                              <Box textAlign="center">
                                <Typography variant="body2" className="total-count" fontWeight="bold" sx={{ fontSize: isSmallMobile ? "0.875rem" : "1rem" }}>
                                  {row.total_count || '0'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: isSmallMobile ? "0.625rem" : "0.75rem" }}>
                                  Total
                                </Typography>
                              </Box>
                              <Box textAlign="center">
                                <Typography variant="body2" className="success-count" fontWeight="bold" sx={{ fontSize: isSmallMobile ? "0.875rem" : "1rem" }}>
                                  {row.success_count || '0'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: isSmallMobile ? "0.625rem" : "0.75rem", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <CheckCircleIcon sx={{ fontSize: isSmallMobile ? '12px' : '14px', color: '#2e7d32' }} />
                                  Sucesso
                                </Typography>
                              </Box>
                              <Box textAlign="center">
                                <Typography variant="body2" className="failure-count" fontWeight="bold" sx={{ fontSize: isSmallMobile ? "0.875rem" : "1rem" }}>
                                  {row.failure_count || '0'}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: isSmallMobile ? "0.625rem" : "0.75rem", display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                                  <ErrorIcon sx={{ fontSize: isSmallMobile ? '12px' : '14px', color: '#d32f2f' }} />
                                  Falhas
                                </Typography>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </Box>
                )}
                <Box display="flex" justifyContent="center" mt={2}>
                  <Pagination
                    count={totalPages}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                  />
                </Box>
              </>
            )}
          </>
        )}

        {/* Menus de contexto */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl) && openMenuId}
          onClose={handleMenuClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          PaperProps={{
            sx: {
              width: { xs: 'calc(100vw - 24px)', sm: 'auto' },
              maxWidth: { xs: 360, sm: 'none' },
              '& .MuiMenuItem-root': {
                minHeight: 44,
                fontSize: { xs: '0.92rem', sm: '0.875rem' },
                py: { xs: 1.1, sm: 0.75 },
              },
            }
          }}
        >
          {/* Ações sempre disponíveis */}
          <MenuItem onClick={() => {
            handleBookIconClick(selectedRow);
            handleMenuClose();
          }}>
            <BookIcon sx={{ mr: 1, fontSize: '18px', color: '#2196f3' }} />
            {t('disparador.menu.viewDetails')}
          </MenuItem>
          
          {selectedRow?.link_file && (
            <MenuItem onClick={() => handleDownloadClick(selectedRow.id, selectedRow.link_file)}>
              <CloudDownloadIcon sx={{ mr: 1, fontSize: '18px', color: '#4caf50' }} />
              {isDownloading[selectedRow?.id] ? t('disparador.menu.downloading') : t('disparador.menu.downloadCSV')}
            </MenuItem>
          )}
          
          {selectedRow?.result_link_file && (
            <MenuItem onClick={() => handleExportReportClick(selectedRow.result_link_file)}>
              <AssessmentIcon sx={{ mr: 1, fontSize: '18px', color: '#ff9800' }} />
              {t('disparador.menu.exportReport')}
            </MenuItem>
          )}
          
          {/* Duplicar campanha - disponível apenas se passou menos de 7 dias do disparo e não for WABA */}
          {selectedRow?.started_at && 
           new Date(selectedRow.started_at) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && 
           selectedRow?.trigger_type !== 'WABA' && (
            <MenuItem onClick={() => {
              handleDuplicateClick(selectedRow.id, selectedRow);
              handleMenuClose();
            }}>
              <ContentCopyIcon sx={{ mr: 1, fontSize: '18px', color: '#9c27b0' }} />
              {t('disparador.menu.duplicateCampaign')}
            </MenuItem>
          )}

          {/* Duplicar campanha WABA - apenas para disparos WABA */}
          {selectedRow?.started_at && 
           new Date(selectedRow.started_at) >= new Date(Date.now() - 7 * 24 * 60 * 60 * 1000) && 
           selectedRow?.trigger_type === 'WABA' && (
            <MenuItem onClick={() => {
              handleDuplicateClick(selectedRow.id, selectedRow);
              handleMenuClose();
            }}>
              <ContentCopyIcon sx={{ mr: 1, fontSize: '18px', color: '#9c27b0' }} />
              {t('disparador.menu.duplicateCampaign')}
            </MenuItem>
          )}
          
          {/* Cancelar agendamento - apenas para status 4 (Agendado) */}
          {selectedRow?.status_id === 4 && (
            <MenuItem 
              onClick={() => {
                handleCancelClick(selectedRow.id);
                handleMenuClose();
              }}
            >
              <CancelIcon sx={{ mr: 1, fontSize: '18px', color: '#ff9800' }} />
              {t('disparador.menu.cancelScheduling')}
            </MenuItem>
          )}
          
          {/* Interromper disparo - apenas para status 3 (Em execução) */}
          {selectedRow?.status_id === 3 && (
            <MenuItem 
              onClick={() => {
                handleInterruptClick(selectedRow.id);
                handleMenuClose();
              }}
            >
              <StopIcon sx={{ mr: 1, fontSize: '18px', color: '#f44336' }} />
              {t('disparador.menu.interruptDispatch')}
            </MenuItem>
          )}
        </Menu>

        <Modal
          open={advancedFiltersOpen}
          onClose={handleCloseAdvancedFilters}
          aria-labelledby="advanced-filters-title"
          aria-describedby="advanced-filters-description"
        >
          <Box
            sx={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(-50%, -50%)',
              width: { xs: 'calc(100% - 16px)', sm: '720px' },
              maxWidth: '95vw',
              bgcolor: 'background.paper',
              borderRadius: '10px',
              boxShadow: 24,
              p: { xs: 2, sm: 3 },
              maxHeight: { xs: '92vh', sm: '85vh' },
              overflowY: 'auto',
            }}
          >
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
              <Typography id="advanced-filters-title" variant="h6" component="h2" sx={{ fontWeight: 500 }}>
                {t('disparador.advancedFilters') || 'Filtros avançados'}
              </Typography>
              <IconButton onClick={handleCloseAdvancedFilters}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.8, fontWeight: 600 }}>
                  {t('disparador.filterCampaignName') || 'Filtrar por campanha'}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('disparador.searchCampaignPlaceholder') || 'Pesquisar campanha'}
                  value={draftCampaignName}
                  onChange={(event) => setDraftCampaignName(event.target.value)}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.8, fontWeight: 600 }}>
                  {t('disparador.filterChannel') || 'Filtrar por canal'}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={t('disparador.filterChannel') || 'Filtrar por canal'}
                  value={draftChannelName}
                  onChange={(event) => setDraftChannelName(event.target.value)}
                />
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.8, fontWeight: 600 }}>
                  {t('disparador.filterResponsible') || 'Filtrar por atendente'}
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={advancedUsersOptions}
                  value={draftSelectedUsers}
                  loading={isLoadingUsersOptions || isLoadingMoreUsers}
                  getOptionLabel={(option) => option.name || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options) => options}
                  onInputChange={handleUsersInputChange}
                  onChange={(event, value) => setDraftSelectedUsers(value)}
                  renderTags={() => null}
                  ListboxProps={{
                    onScroll: handleUsersListboxScroll,
                    style: { maxHeight: '300px' }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('disparador.searchResponsiblePlaceholder') || 'Pesquisar responsável'}
                    />
                  )}
                />
                {draftSelectedUsers.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={0.8} mt={1}>
                    {draftSelectedUsers.map((option) => (
                      <Chip
                        key={option.id}
                        label={option.name}
                        size="small"
                        onDelete={() => setDraftSelectedUsers((prev) => prev.filter((item) => item.id !== option.id))}
                        sx={selectedChipSx}
                      />
                    ))}
                  </Box>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 0.8, fontWeight: 600 }}>
                  {t('disparador.filterTemplate') || 'Filtrar por template'}
                </Typography>
                <Autocomplete
                  multiple
                  size="small"
                  options={advancedTemplatesOptions}
                  value={draftSelectedTemplates}
                  loading={isLoadingTemplatesOptions || isLoadingMoreTemplates}
                  getOptionLabel={(option) => option.tag || ''}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options) => options}
                  onInputChange={handleTemplatesInputChange}
                  onChange={(event, value) => setDraftSelectedTemplates(value)}
                  renderTags={() => null}
                  ListboxProps={{
                    onScroll: handleTemplatesListboxScroll,
                    style: { maxHeight: '300px' }
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      placeholder={t('disparador.searchTemplatePlaceholder') || 'Pesquisar template'}
                    />
                  )}
                />
                {draftSelectedTemplates.length > 0 && (
                  <Box display="flex" flexWrap="wrap" gap={0.8} mt={1}>
                    {draftSelectedTemplates.map((option) => (
                      <Chip
                        key={option.id}
                        label={option.tag}
                        size="small"
                        onDelete={() => setDraftSelectedTemplates((prev) => prev.filter((item) => item.id !== option.id))}
                        sx={selectedChipSx}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            </Box>

            <Box display="flex" justifyContent="center" alignItems="center" gap={1.5} mt={3} flexDirection={{ xs: 'column-reverse', sm: 'row' }}>
              <Button variant="outlined" onClick={handleCloseAdvancedFilters} fullWidth={isSmallMobile}>
                {t('disparador.cancelar') || 'Cancelar'}
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyAdvancedFilters}
                fullWidth={isSmallMobile}
                sx={{ backgroundColor: '#1976d2' }}
              >
                {t('disparador.applyFilters') || 'Filtrar'}
              </Button>
            </Box>
          </Box>
        </Modal>

        <Modal
          open={interruptModalOpen}
          onClose={handleInterruptModalClose}
          aria-labelledby="modal-interrupt-confirmation"
          aria-describedby="modal-interrupt-confirmation-description"
        >
          <div className="modal">
            <div
              className="modal-content"
              style={{
                width: '600px',
                maxWidth: '90vw',
                minHeight: '200px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                borderRadius: '10px',
                backgroundColor: 'white',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                padding: '20px',
                position: 'absolute',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography id="modal-interrupt-confirmation" variant="h6" component="h2">
                  {t('disparador.modal.confirmInterruptTitle')}
                </Typography>
                <IconButton onClick={handleInterruptModalClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography id="modal-interrupt-confirmation-description" variant="body1" mb={2}>
                {t('disparador.modal.confirmInterruptMessage')}
              </Typography>
              <Box display="flex" justifyContent="flex-end" gap="24px">
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleInterruptModalClose();
                    handleMenuClose();
                  }}
                  sx={{
                    width: '267px',
                    height: '32px',
                    minWidth: '64px',
                    borderRadius: '4px',
                    borderWidth: '1px',
                    padding: '6px 16px',
                    borderColor: '#0000C8',
                    color: '#0000C8',
                    backgroundColor: 'white',
                    '&:hover': {
                      borderColor: '#0000A0',
                      backgroundColor: 'rgba(0, 0, 200, 0.04)',
                    },
                  }}
                >
                  {t('disparador.modal.keepDispatches')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmInterrupt}
                  disabled={isInterrupting}
                  sx={{
                    width: '267px',
                    height: '32px',
                    minWidth: '64px',
                    borderRadius: '4px',
                    borderWidth: '1px',
                    padding: '6px 16px',
                    backgroundColor: '#0000C8',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#0000A0',
                    },
                  }}
                >
                  {isInterrupting ? <CircularProgress size={24} style={{ color: 'white' }} /> : t('disparador.modal.confirmCancelButton')}
                </Button>
              </Box>
            </div>
          </div>
        </Modal>

        <Modal
          open={modalOpen}
          onClose={handleModalClose}
          aria-labelledby="modal-type-selection"
          aria-describedby="modal-type-selection-description"
        >
          <div className="modal">
            <div
              className="modal-content"
              style={{
                width: '500px',
                margin: '0 auto',
                padding: '20px',
                borderRadius: '8px',
                backgroundColor: 'white',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography id="modal-type-selection" variant="h6" component="h2">
                  {t('disparador.modal.createCampaign')}
                </Typography>
                <IconButton onClick={handleModalClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Autocomplete
                options={channels}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
                loading={isLoadingMoreChannels}
                inputValue={createModalChannelSearch}
                onInputChange={(event, value) => setCreateModalChannelSearch(value)}
                onChange={(event, value) => {
                  if (value) {
                    setSelectedChannel(value);
                    setSecondaryModalOpen(true);
                  }
                }}
                ListboxProps={{
                  onScroll: handleChannelsMenuScroll,
                  style: { maxHeight: '300px' }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={t('disparador.modal.selectChannel') || 'Selecione um canal'}
                    margin="normal"
                    fullWidth
                  />
                )}
              />
            </div>
          </div>
        </Modal>

        {selectedChannel && selectedChannel.channel_id === 6 && (
          <WabaModal 
            open={secondaryModalOpen} 
            onClose={closeSecondaryModal} 
            channelId={selectedChannel.id} 
            channelName={selectedChannel.name}
            isOperadorOnly={isOperadorOnly}
            currentUserId={currentUserId}
          />
        )}

        {selectedChannel && selectedChannel.channel_id === 1 && (
          <BrokerModal 
            open={secondaryModalOpen} 
            onClose={closeSecondaryModal} 
            channelId={selectedChannel.id} 
            channelName={selectedChannel.name}
            isOperadorOnly={isOperadorOnly}
            currentUserId={currentUserId}
          />
        )}

        {selectedChannel && selectedChannel.channel_id === 5 && (
          <InstaModal 
            open={secondaryModalOpen} 
            onClose={closeSecondaryModal} 
            channelId={selectedChannel.id} 
            channelName={selectedChannel.name}
            isOperadorOnly={isOperadorOnly}
            currentUserId={currentUserId}
          />
        )}

        <TriggerDetailsModal
          open={detailsModalOpen}
          onClose={closeDetailsModal}
          details={selectedTriggerDetails}
        />

        <DuplicateModals 
          duplicateModalOpen={duplicateModalOpen}
          duplicateWabaModalOpen={duplicateWabaModalOpen}
          duplicateBrokerModalOpen={duplicateBrokerModalOpen}
          duplicateDateTime={duplicateDateTime}
          setDuplicateDateTime={setDuplicateDateTime}
          isDuplicating={isDuplicating}
          wabaTemplates={wabaTemplates}
          selectedTemplate={selectedTemplate}
          setSelectedTemplate={setSelectedTemplate}
          // Props específicas do BROKER
          brokerMensagem={brokerMensagem}
          setBrokerMensagem={setBrokerMensagem}
          brokerMensagemRapida={brokerMensagemRapida}
          setBrokerMensagemRapida={setBrokerMensagemRapida}
          brokerMensagensRapidas={brokerMensagensRapidas}
          brokerImagem={brokerImagem}
          setBrokerImagem={setBrokerImagem}
          brokerImagemUrl={brokerImagemUrl}
          setBrokerImagemUrl={setBrokerImagemUrl}
          wabaChannelId={wabaChannelId}
          brokerChannelId={brokerChannelId}
          onClose={handleDuplicateModalClose}
          onConfirmDuplicate={handleConfirmDuplicate}
          onConfirmDuplicateWaba={handleConfirmDuplicateWaba}
          onConfirmDuplicateBroker={handleConfirmDuplicateBroker}
        />

        <Modal
          open={cancelModalOpen}
          onClose={handleCancelModalClose}
          aria-labelledby="modal-cancel-confirmation"
          aria-describedby="modal-cancel-confirmation-description"
        >
          <div className="modal">
            <div
              className="modal-content"
              style={{
                width: '600px',
                maxWidth: '90vw',
                minHeight: '200px',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                borderRadius: '10px',
                backgroundColor: 'white',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                padding: '20px',
                position: 'absolute',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography id="modal-cancel-confirmation" variant="h6" component="h2">
                  {t('disparador.modal.confirmCancelTitle')}
                </Typography>
                <IconButton onClick={handleCancelModalClose}>
                  <CloseIcon />
                </IconButton>
              </Box>
              <Typography id="modal-cancel-confirmation-description" variant="body1" mb={2}>
                {t('disparador.modal.confirmCancelMessage')}
              </Typography>
              <Box display="flex" justifyContent="flex-end" gap="24px">
                <Button
                  variant="outlined"
                  onClick={() => {
                    handleCancelModalClose();
                    handleMenuClose();
                  }}
                  sx={{
                    width: '267px',
                    height: '32px',
                    minWidth: '64px',
                    borderRadius: '4px',
                    borderWidth: '1px',
                    padding: '6px 16px',
                    borderColor: '#0000C8',
                    color: '#0000C8',
                    backgroundColor: 'white',
                    '&:hover': {
                      borderColor: '#0000A0',
                      backgroundColor: 'rgba(0, 0, 200, 0.04)',
                    },
                  }}
                >
                  {t('disparador.modal.keepScheduling')}
                </Button>
                <Button
                  variant="contained"
                  onClick={handleConfirmCancel}
                  disabled={isCanceling}
                  sx={{
                    width: '267px',
                    height: '32px',
                    minWidth: '64px',
                    borderRadius: '4px',
                    borderWidth: '1px',
                    padding: '6px 16px',
                    backgroundColor: '#0000C8',
                    color: 'white',
                    '&:hover': {
                      backgroundColor: '#0000A0',
                    },
                  }}
                >
                  {isCanceling ? <CircularProgress size={24} style={{ color: 'white' }} /> : t('disparador.modal.confirmCancelButton')}
                </Button>
              </Box>
            </div>
          </div>
        </Modal>
      </DisparadorContainer>
    </>
  );
};

export default Disparador;